import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useDeletePricingPlanCap } from 'src/features/pricingPlan/api/caps/useDeletePricingPlanCap';
import { LIST_PRICING_PLAN_CAPS_RESOURCE } from 'src/features/pricingPlan/api/caps/useListPricingPlanCaps';
import { PricingPlanCap } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';

interface PricingPlanCapFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedCap: PricingPlanCap;
    children?: React.ReactNode;
}

function DeletePricingPlanCapFormModal<TData>({ selectedCap, children }: PricingPlanCapFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate: deleteCap } = useDeletePricingPlanCap({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLAN_CAPS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(pricingPlanMessages.deleteCapSuccess),
                description: formatMessage(pricingPlanMessages.deleteCapSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...generalMessages.delete },
                subTitle: { ...pricingPlanMessages.deleteCapSubtitle },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...pricingPlanMessages.confirmDeleteCap },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => {
                    return (deleteCap({ planId, capId: selectedCap.id, parkingId }), setIsModalOpen(false));
                },
            }}
            children={children}
        />
    );
}

export default DeletePricingPlanCapFormModal;
